import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from '@/lib/api';

export default function TestScraperPage() {
  const [url, setUrl] = useState("https://www.airbnb.com/rooms/43813369");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("tester");

  const handleScrape = async () => {
    if (!url) {
      setError("Please enter an Airbnb URL");
      return;
    }

    if (!url.includes('airbnb.com')) {
      setError("Please enter a valid Airbnb URL");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      // Call the test endpoint directly with a marker for tracking in logs
      console.log("Sending test request with URL:", url);
      const data = { url, clientId: 'test-page-v2' };
      const response = await apiRequest('POST', '/test/scrape-listing', data);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || `Server responded with status: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      const dataResponse = await response.json();
      console.log("Scraping results:", dataResponse);
      
      // Check if we get the new response format or the old one
      const propertyData = dataResponse.property || dataResponse;
      setResult(propertyData);
      setActiveTab("results");
    } catch (err: any) {
      console.error("Scraping error:", err);
      setError(err.message || "Failed to scrape Airbnb listing");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCode = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <div className="container mx-auto py-10">
      <h1 className="text-3xl font-bold mb-6">StayMiddle Airbnb Scraper Test Page</h1>
      <p className="text-muted-foreground mb-8">
        This page allows you to test the Airbnb scraping functionality without authentication.
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tester">Scraper</TabsTrigger>
          <TabsTrigger value="results" disabled={!result}>Results</TabsTrigger>
        </TabsList>

        <TabsContent value="tester" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Test Airbnb Scraper</CardTitle>
              <CardDescription>
                Enter an Airbnb URL to test the scraping functionality.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="url">Airbnb URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://www.airbnb.com/rooms/..."
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleScrape}
                      disabled={isLoading}
                      className="bg-gold hover:bg-gold-dark"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Scraping...
                        </>
                      ) : "Scrape"}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This will test the Airbnb scraping functionality without requiring authentication.
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results" className="mt-6 space-y-6">
          {result && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2 h-5 w-5" />
                    Scraping Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Property Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium">Title</p>
                          <p className="text-base">{result.title}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Area</p>
                          <p className="text-base">{result.area}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Bedrooms</p>
                          <p className="text-base">{result.bedrooms}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Bathrooms</p>
                          <p className="text-base">{result.bathrooms}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Max Guests</p>
                          <p className="text-base">{result.maxGuests}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Base Price</p>
                          <p className="text-base">AED {result.basePrice / 100}</p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">Description</h3>
                      <p className="text-base whitespace-pre-wrap">{result.description}</p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">Features</h3>
                      <div className="flex flex-wrap gap-2">
                        {result.features && result.features.map((feature: string, index: number) => (
                          <div key={index} className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm">
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">Images ({result.images?.length || 0})</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {result.images && result.images.map((image: string, index: number) => (
                          <div key={index} className="aspect-square rounded-md overflow-hidden border">
                            <img src={image} alt={`Property image ${index + 1}`} className="object-cover w-full h-full" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Raw JSON Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    readOnly
                    className="font-mono text-xs h-[400px] overflow-auto"
                    value={formatCode(result)}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}